// In Results.tsx
// UNPAVED - Pavements Results Component - è il file dei results di unpaved

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { IResultsProps } from './IResults';
// import { useSelector } from 'react-redux'; // useAppSelector is preferred
import { Grid, Typography } from '@mui/material';
import { RootState } from '../../../../reduxService'; // Adatta
import { useTranslation } from 'react-i18next';
import TireAndLayers, { ILayerData, IGeogridInfo } from './TireAndLayers'; // Importa Tipi
import { unit as unitEnum, EProjectType } from '../../../enum/project'; // Adatta, Import EProjectType
// import { selectDynamicProject } from '../../../Dynamic/store/project/projectslice'; // No longer needed directly if using dynamicProject from RootState
import PavementLineChartComponent from './PavementLineChartComponent';
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
// import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice'; // No longer needed if projectInfo comes from dynamicProject
import { useAppSelector } from '../../../../reduxService/hooks';
import { conversionLabel } from '../../../conversion';
import './Results.scss';
import { logDebug, logWarn, logError, LogTag } from '../../../utils/utils';
// Use IContainerYardData as suggested by error, and ensure IDynamicProjectOutputData is correctly defined in IApiResponse.ts
import { IContainerYardData, IDynamicProjectOutputData, IBCRData, IDeckAnalysisDataItem, IParkingDeckData, ILoadDataWheel, IStaticLoadData, IUnpavedData } from '../../../api/IApiResponse'; // Added IParkingDeckData, ILoadDataWheel, IStaticLoadData, IUnpavedData

interface Point {
    x: number;
    y: number;
}

// Extend IDeckAnalysisDataItem locally to include optional bcr for this chart logic
interface IDeckAnalysisDataItemWithBcr extends IDeckAnalysisDataItem {
    bcr?: number;
}

const Results = (_props: IResultsProps) => {
    const rawOutput = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData as IDynamicProjectOutputData | undefined);
    // const projectInfo = useAppSelector(selectProjectInfo); // Sourced from dynamicProject now
    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project); // Get the whole dynamicProject state
    const { t } = useTranslation();

    const [unreinforcedStageHeight, setUnreinforcedStageHeight] = useState<number | undefined>(undefined);
    const [unreinforcedSubgradeY, setUnreinforcedSubgradeY] = useState<number | undefined>(undefined);

    const [unreinforcedDeckTable, setUnreinforcedDeckTable] = useState<Point[]>([]);
    const [reinforcedDeckTable, setReinforcedDeckTable] = useState<Point[]>([]);
    const [bcrDeckTable, setBcrDeckTable] = useState<Point[]>([]);

    const currentProjectType = dynamicProject.data?.type; // Get project type
    //get project method
    const currentProjectMethod = dynamicProject.data?.method; // Get project method
    logDebug('PavResults', 'Current project type:', currentProjectType, LogTag.PROJECT_INFO);
    logDebug('PavResults', 'Current project method:', currentProjectMethod, LogTag.PROJECT_INFO);

    // --- Check if geotextile was selected (for showing geotextile diagram) ---
    const hasGeotextileSelected = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved) return false;
        const unpavedData = dynamicProject.data?.projectInfo?.data as IUnpavedData;
        if (!unpavedData) return false;
        // Check both giroud and leng methods for selectedGeotextile in geoTextile group
        const method = currentProjectMethod || 'giroud';

        // FLAT structure: geoTextile is directly on unpavedData
        const selectedGeotextile = (unpavedData as any)?.geoTextile?.selectedGeotextile;

        logDebug(
            'PavResults',
            'Checking geotextile selection:',
            {
                method,
                selectedGeotextile,
                hasSelection: selectedGeotextile && selectedGeotextile.trim() !== '',
            },
            LogTag.VALIDATION
        );
        return selectedGeotextile && selectedGeotextile.trim() !== '';
    }, [currentProjectType, currentProjectMethod, dynamicProject.data?.projectInfo?.data]);

    // --- Get selected geotextile product name for dynamic labeling ---
    const selectedGeotextileProduct = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved) return null;
        const unpavedData = dynamicProject.data?.projectInfo?.data as IUnpavedData;
        if (!unpavedData) return null;
        const method = currentProjectMethod || 'giroud';

        // FLAT structure: geoTextile is directly on unpavedData
        const selectedGeotextile = (unpavedData as any)?.geoTextile?.selectedGeotextile;

        const result = selectedGeotextile && selectedGeotextile.trim() !== '' && selectedGeotextile !== 'none_selected' ? selectedGeotextile : null;

        return result;
    }, [currentProjectType, currentProjectMethod, dynamicProject.data?.projectInfo?.data]);

    // --- Get selected geogrid product name for dynamic labeling ---
    const selectedGeogridProduct = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved) return null;
        const unpavedData = dynamicProject.data?.projectInfo?.data as IUnpavedData;
        if (!unpavedData) return null;

        const method = currentProjectMethod || 'giroud';

        // FLAT structure: geoGrid is directly on unpavedData
        const selectedGeogrid = (unpavedData as any)?.geoGrid?.selectedGeogrid;

        const result = selectedGeogrid && selectedGeogrid.trim() !== '' && selectedGeogrid !== 'none_selected' ? selectedGeogrid : null;

        return result;
    }, [currentProjectType, currentProjectMethod, dynamicProject.data?.projectInfo?.data]);

    // 🆕 SIMPLIFIED APPROACH: Redux contains data already in the correct unit
    // No need for conversion - the data from Redux is already in the display unit
    const processedOutput = useMemo(() => {
        logDebug('PavResults', 'Using output data directly from API - conversion handled at Redux level', undefined, LogTag.UNIT_CONVERSION);
        return rawOutput;
    }, [rawOutput]);

    logDebug('PavResults', 'Output for parking deck check:', processedOutput?.deckDesign, LogTag.DATA_TRANSFORM);

    // Source inputs from dynamicProject.data.projectInfo.data
    const projectInputs = useMemo(() => {
        if (dynamicProject.data?.type === EProjectType.container_yard) {
            return dynamicProject.data.projectInfo?.data as IContainerYardData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.parking_deck) {
            return dynamicProject.data.projectInfo?.data as IParkingDeckData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.unpaved) {
            return dynamicProject.data.projectInfo?.data;
        }
        return undefined;
    }, [dynamicProject.data]);

    const getMeasurementUnit = (unit: string[]) => {
        const unitSystem = dynamicProject.data?.unit || 'Metric';
        return unit[unitSystem === 'Metric' ? 0 : 1];
    };

    logDebug('PavResults', 'Output data:', processedOutput, LogTag.DATA_TRANSFORM);
    logDebug('PavResults', 'Project inputs from dynamicProject:', projectInputs, LogTag.DATA_TRANSFORM);

    const loadResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - FLAT structure
        if (currentProjectType === EProjectType.unpaved) {
            // FLAT structure: data is directly on projectInputs, not nested under method
            const unpavedData = projectInputs as any;
            if (!unpavedData?.load) return [];

            return [
                [t('design_axle_load', 'Design Axle Load'), 'W', unpavedData.load.designAxleLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tyre_inflation_pressure', 'Tyre Inflation Pressure'), 'p', unpavedData.load.tyreInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('equivalent_standard_axle_load', 'ESAL'), 'Pa', unpavedData.load.esal?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('number_of_passages', 'Number of Passages'), 'Np', unpavedData.load.numPassages?.toString() || '', ''],
                [t('allowable_rut_depth', 'Allowable Rut Depth'), 's', unpavedData.load.allowableRutDepth?.toString() || '', 'mm'],
            ];
        }

        const bcrData = (projectInputs as any).bcr;
        if (!bcrData) return [];

        if (currentProjectType === EProjectType.container_yard) {
            const cyBcrLoad = (bcrData as IBCRData)?.load;
            return [
                [t('deckWidth'), 'B', cyBcrLoad?.deckWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('deckLength'), 'L', cyBcrLoad?.deckLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('uniformLoadRes'), 'q', cyBcrLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('safetyFactor'), 'SF', bcrData?.fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdBcrLoad = bcrData.load as ILoadDataWheel; // Type assertion
            return [
                [t('wheel_load', 'Wheel Load'), 'P', pdBcrLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tire_inflation_pressure', 'Tire Inflation Pressure'), 'p', pdBcrLoad?.tireInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('safetyFactor'), 'SF', bcrData.fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        }
        return [];
    }, [projectInputs, currentProjectType, currentProjectMethod, t, dynamicProject.data?.unit]);

    const subgradeResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - FLAT structure
        if (currentProjectType === EProjectType.unpaved) {
            // FLAT structure: data is directly on projectInputs
            const unpavedData = projectInputs as any;
            if (!unpavedData?.subgrade) return [];

            const subgradeData = unpavedData.subgrade;
            const inputMethod = subgradeData.subgradeInputMethod;

            if (inputMethod === 'cbr') {
                return [
                    [t('cbr_base_course', 'CBR Base Course'), 'CBRbc', subgradeData.cbrBaseCourse?.toString() || '', '%'],
                    [t('cbr_subgrade', 'CBR Subgrade'), 'CBRsg', subgradeData.cbrSubgrade?.toString() || '', '%'],
                ];
            } else if (inputMethod === 'modulus') {
                return [
                    [t('ev2_base_course', 'Ev2 Base Course'), 'Ev2bc', subgradeData.ev2BaseCourse?.toString() || '', 'MPa'],
                    [t('ev2_subgrade', 'Ev2 Subgrade'), 'Ev2sg', subgradeData.ev2Subgrade?.toString() || '', 'MPa'],
                ];
            }
            return [];
        }

        const bcrData = (projectInputs as any).bcr;
        if (!bcrData) return [];

        // Access subgrade data using the correct runtime key 'subgradeCharacteristics'
        // The interface ISubgradeCharatteristic might have a typo, so we cast to 'any' for this access.
        const bcrSubgrade = (bcrData as any).subgradeCharacteristics;
        const bcrEmbarkment = bcrData.embankmentCharacteristics; // Assuming this access is correct as per runtime data

        // Log to verify the data being accessed
        logDebug('PavResults', 'subgradeResults bcrData:', bcrData, LogTag.CALCULATIONS);
        logDebug('PavResults', 'subgradeResults bcrSubgrade:', bcrSubgrade, LogTag.CALCULATIONS);
        logDebug('PavResults', 'subgradeResults bcrEmbarkment:', bcrEmbarkment, LogTag.CALCULATIONS);

        return [
            [t('unitWeightRes'), 'γ', bcrSubgrade?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
            [t('frictionAngleRes'), 'φ', bcrSubgrade?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('cohesionRes'), 'c', bcrSubgrade?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            // These lines use bcrEmbarkment, ensure it's correctly populated if these are also missing.
            // The primary issue reported is with subgrade properties like unitWeight, frictionAngle, cohesion.
        ];
    }, [projectInputs, currentProjectType, currentProjectMethod, t, dynamicProject.data?.unit]);

    const embarkmentResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - no embankment data for unpaved roads
        if (currentProjectType === EProjectType.unpaved) {
            return [];
        }

        const bcrData = (projectInputs as any).bcr;
        if (!bcrData) return [];

        const bcrEmbarkment = bcrData.embarkmentCharacteristics;
        return [
            [t('embarkment_weight', { unit_measure: '' }).trim(), 'γ', bcrEmbarkment?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
            [t('embarkment_friction_angle', { unit_measure: '' }).trim(), 'φ', bcrEmbarkment?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('embarkment_cohesion', { unit_measure: '' }).trim(), 'c', bcrEmbarkment?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            [t('loadSpreadingAngleRes'), 'αᵤ', bcrEmbarkment?.loadSpreadingAngleU?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
            [t('loadSpreadingAngleRes'), 'αᵣ', bcrEmbarkment?.loadSpreadingAngleR?.toString() || '', getMeasurementUnit(conversionLabel.deg)],
        ];
    }, [projectInputs, currentProjectType, t, dynamicProject.data?.unit]);

    // --- STATIC method input data (unified in one table, show only the selected subgrade input) ---
    const staticInputResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // Handle unpaved road data - no static method for unpaved roads
        if (currentProjectType === EProjectType.unpaved) {
            return [];
        }

        const staticData = (projectInputs as any).static;
        if (!staticData) return [];

        // Determine subgrade input method based on which property is present
        const subgradeRows: any[] = [];
        if (staticData.subgrade?.cbrSubgrade !== undefined) {
            subgradeRows.push([t('static_cbr', 'CBR'), 'CBR', staticData.subgrade.cbrSubgrade.toString(), '%']);
        } else if (staticData.subgrade?.deformationModulus !== undefined) {
            subgradeRows.push([t('static_deformation_modulus', 'Deformation Modulus'), 'E', staticData.subgrade.deformationModulus.toString(), 'MPa']);
        }

        let loadRows: any[] = [];
        if (currentProjectType === EProjectType.container_yard) {
            const cyStaticLoad = staticData.load as IStaticLoadData; // Type assertion
            loadRows = [
                [t('static_deck_half_width', 'Deck Half Width'), "B'", cyStaticLoad?.deckHalfWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('static_deck_half_length', 'Deck Half Length'), "L'", cyStaticLoad?.deckHalfLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
                [t('static_uniform_load', 'Uniform Load'), 'q', cyStaticLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdStaticLoad = staticData.load as ILoadDataWheel; // Type assertion
            loadRows = [
                [t('wheel_load', 'Wheel Load'), 'P', pdStaticLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tire_inflation_pressure', 'Tire Inflation Pressure'), 'p', pdStaticLoad?.tireInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
            ];
        }

        return [...loadRows, ...subgradeRows, [t('static_safety_factor', 'Safety Factor'), 'FS', staticData.fsDesign?.fsDesign?.toString() || '', '-']];
    }, [projectInputs, currentProjectType, t, dynamicProject.data?.unit]);

    // --- Prepara i dati per il diagramma NON RINFORZATO (Sinistra) ---
    const unreinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hu = processedOutput.deckDesign.unreinforcedHeight;
        if (hu === undefined) {
            logWarn('CYResults', 'Final unreinforcedHeight (hu) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }
        const baseThickUnreinforced = hu;
        const subgradeVisible = 0.5;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickUnreinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        // Nessun geogrid per il non rinforzato
        return { baseCourse, subgrade, rightDimensionValue: hu, unitLabel, geogrid: undefined };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per il diagramma RINFORZATO (Destra) ---
    const reinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hr = processedOutput.deckDesign.reinforcedHeight;
        const currentSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage;
        if (hr === undefined) {
            logWarn('CYResults', 'Final reinforcedHeight (hr) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }
        const baseThickReinforced = hr;
        const subgradeVisible = 0.5;
        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: firstBaseGeogrid.position ?? 0,
                  label: firstBaseGeogrid.selectedGeogrid,
              }
            : undefined;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickReinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: hr, savingPercentage: currentSavingPercentage, geogrid: geogridInfo, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma UNSTABILIZED ---
    const unpavedUnstabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as any;
        if (!unpavedOutput.unstabilisedRoadThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.unstabilisedRoadThickness;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: thickness, unitLabel, geogrid: undefined };
    }, [dynamicProject, processedOutput, currentProjectType, currentProjectMethod, t]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma GEOTEXTILE ---
    const unpavedGeotextileGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !hasGeotextileSelected || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as any;
        if (!unpavedOutput.geotextileStabilisedThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.geotextileStabilisedThickness;
        const savingPercentage = unpavedOutput.thicknessSavingGeotextile;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        // Note: For geotextile, we use the geogrid object to position the line, but the actual label
        // is passed separately via geotextileLabel prop to TireAndLayers
        const geogrid: IGeogridInfo = {
            positionFromBottomOfBase: baseThick,
            label: undefined, // Not used for geotextile - label comes from geotextileLabel prop
            // color: '#FF0000',
        };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: baseThick, savingPercentage, geogrid, unitLabel };
    }, [dynamicProject, processedOutput, currentProjectType, currentProjectMethod, t, selectedGeotextileProduct]);

    // --- Prepara i dati per UNPAVED GIROUD - Diagramma GEOGRID ---
    const unpavedGeogridGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || !processedOutput || !dynamicProject.data) return null;
        const unpavedOutput = processedOutput as any;
        if (!unpavedOutput.geogridStabilisedThickness) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const thickness = unpavedOutput.geogridStabilisedThickness;
        const savingPercentage = unpavedOutput.thicknessSavingGeogrid;
        const baseThick = thickness;
        const subgradeVisible = 0.5;
        const geogrid: IGeogridInfo = {
            positionFromBottomOfBase: baseThick,
            label: selectedGeogridProduct || 'Geogrid', // Use actual product name or fallback
            // color: '#00FF00',
        };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThick, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };
        return { baseCourse, subgrade, rightDimensionValue: baseThick, savingPercentage, geogrid, unitLabel };
    }, [dynamicProject, processedOutput, currentProjectType, currentProjectMethod, t, hasGeotextileSelected, selectedGeogridProduct]);

    useEffect(() => {
        const mapDeckAnalysisData = (data: IDeckAnalysisDataItem[] | undefined): Point[] => {
            if (!data) return [];
            try {
                return data.map(item => ({
                    x: item.h,
                    y: item.fs,
                }));
            } catch (error) {
                logError('CYResults', 'Errore durante la mappatura dei dati di analisi del deck:', error, LogTag.ERROR_HANDLING);
                return [];
            }
        };

        if (processedOutput?.deckDesign?.unreinforcedDeckAnalysisData) {
            setUnreinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.unreinforcedDeckAnalysisData));
        } else {
            setUnreinforcedDeckTable([]);
        }

        if (processedOutput?.deckDesign?.reinforcedDeckAnalysisData) {
            setReinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.reinforcedDeckAnalysisData));
        } else {
            setReinforcedDeckTable([]);
        }

        // Use bcr field from reinforcedDeckAnalysisData for setBcrDeckTable (with type assertion)
        if (processedOutput?.deckDesign?.reinforcedDeckAnalysisData) {
            setBcrDeckTable(
                (processedOutput.deckDesign.reinforcedDeckAnalysisData as IDeckAnalysisDataItemWithBcr[])
                    .filter(item => typeof item.bcr === 'number')
                    .map(item => ({
                        x: item.h,
                        y: item.bcr as number,
                    }))
            );
        } else {
            setBcrDeckTable([]);
        }
    }, [processedOutput]);

    const deckResults = useMemo(() => {
        if (!processedOutput) return [];

        // Handle unpaved road results
        if (currentProjectType === EProjectType.unpaved) {
            const unpavedOutput = processedOutput as any; // Type assertion for unpaved structure
            const rows = [[t('unstabilised_road_thickness', 'Unstabilised Road Thickness'), <span>h<sub>u</sub></span>, unpavedOutput.unstabilisedRoadThickness?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)]];

            // Add geotextile rows only if geotextile is selected
            if (hasGeotextileSelected) {
                rows.push([
                    t('geotextile_stabilised_thickness', 'Geotextile Stabilised Thickness'),
                    <span>h<sub>gtx</sub></span>,
                    unpavedOutput.geotextileStabilisedThickness?.toString() || '',
                    getMeasurementUnit(conversionLabel.m_ft),
                ]);
            }

            // Always add geogrid row
            rows.push([t('geogrid_stabilised_thickness', 'Geogrid Stabilised Thickness'), <span>h<sub>gg</sub></span>, unpavedOutput.geogridStabilisedThickness?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)]);

            // Add thickness saving rows conditionally
            if (hasGeotextileSelected) {
                rows.push([t('thickness_saving_geotextile', 'Thickness Saving Geotextile'), <span>Δh<sub>gtx</sub></span>, unpavedOutput.thicknessSavingGeotextile?.toFixed(2) || '', '%']);
            }

            rows.push([t('thickness_saving_geogrid', 'Thickness Saving Geogrid'), <span>Δh<sub>gg</sub></span>, unpavedOutput.thicknessSavingGeogrid?.toFixed(2) || '', '%']);

            return rows;
        }

        // Handle container yard/parking deck results
        if (!processedOutput?.deckDesign) return [];
        // Round thicknessSavingPercentage to 3 decimals if present
        let roundedSavingPercentage = '';
        if (typeof processedOutput.deckDesign.thicknessSavingPercentage === 'number') {
            roundedSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage.toFixed(3);
        } else if (processedOutput.deckDesign.thicknessSavingPercentage != null) {
            roundedSavingPercentage = String(processedOutput.deckDesign.thicknessSavingPercentage);
        }
        return [
            [t('unstabilized_deck', { unit_measure: '' }), 'hᵤ', processedOutput.deckDesign.unreinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('stabilized_deck', { unit_measure: '' }), 'hₛ', processedOutput.deckDesign.reinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('thickness_saving', { unit_measure: 'm' }), 'Δh', processedOutput.deckDesign.thicknessSaving?.toString() || '', getMeasurementUnit(conversionLabel.m_ft)],
            [t('thickness_saving_percentage', { unit_measure: 'm' }), 'Δh', roundedSavingPercentage, '%'],
        ];
    }, [processedOutput, currentProjectType, t, dynamicProject.data?.unit]);

    // Compute the maximum h value from all chart data for x-axis scaling
    const computedMaxH = useMemo(() => {
        const allH = [...unreinforcedDeckTable.map(p => p.x), ...reinforcedDeckTable.map(p => p.x), ...bcrDeckTable.map(p => p.x)];
        return allH.length > 0 ? Math.max(...allH) : 0;
    }, [unreinforcedDeckTable, reinforcedDeckTable, bcrDeckTable]);

    // --- STATIC chart data mapping ---
    const staticChartData = useMemo(() => {
        if (currentProjectMethod === 'static' && processedOutput?.deckDesign && Array.isArray((processedOutput.deckDesign as any).iterationTable)) {
            // Estraggo i dati dalla tabella delle iterazioni
            const iterationTable = (processedOutput.deckDesign as any).iterationTable as {
                height: number;
                stabBearingCapacity: number;
                unstabBearingCapacity: number;
                verticalPressure: number;
            }[];

            if (!iterationTable || iterationTable.length === 0) return null;

            // Mappa i dati per ogni curva. Axes are swapped:
            // X-axis: q (kPa) values
            // Y-axis: H (m) values
            const vertPressure = iterationTable.map(item => ({
                x: item.verticalPressure, // X is now Pressure
                y: item.height, // Y is now Height
            }));
            const unstab = iterationTable.map(item => ({
                x: item.unstabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));
            const stab = iterationTable.map(item => ({
                x: item.stabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));

            return { vertPressure, unstab, stab };
        }
        return null;
    }, [currentProjectMethod, processedOutput]);

    // --- PARKING DECK STATIC chart data mapping (cuKPa vs. H) ---
    const parkingDeckStaticChartData = useMemo(() => {
        // Ensure EProjectType.parking_deck is correctly defined and imported
        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && processedOutput?.deckDesign?.cbrLookupTable) {
            const cbrTable = processedOutput.deckDesign.cbrLookupTable;
            if (!Array.isArray(cbrTable) || cbrTable.length === 0) return null;

            const hUnstabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: item.hUnstabilisedM, // Y-axis: hUnstabilisedM
            }));

            const hStabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: item.hStabilisedM, // Y-axis: hStabilisedM
            }));
            logDebug('CYResults', 'parkingDeckStaticChartData prepared:', { hUnstabilisedData, hStabilisedData }, LogTag.CHARTS);
            return { hUnstabilisedData, hStabilisedData };
        }
        return null;
    }, [currentProjectType, currentProjectMethod, processedOutput]);

    // --- UNPAVED GIROUD/LENG INPUTS TABLE ---
    const unpavedInputsTable = useMemo(() => {
        if (currentProjectType !== EProjectType.unpaved || (currentProjectMethod !== 'giroud' && currentProjectMethod !== 'leng') || !projectInputs) return [];

        // FLAT structure: data is directly on projectInputs
        const unpavedData = projectInputs as any;
        if (!unpavedData) return [];

        const rows: any[] = [];

        // Load inputs - handle different field names for Giroud (wheelLoad) vs Leng (designAxleLoad)
        if (unpavedData.load) {
            const axleLoadValue = currentProjectMethod === 'giroud' ? unpavedData.load.wheelLoad?.toString() || '' : unpavedData.load.designAxleLoad?.toString() || '';

            const axleLoadLabel = currentProjectMethod === 'giroud' ? t('wheel_load', 'Wheel Load') : t('design_axle_load', 'Design Axle Load');

            const axleLoadSymbol = currentProjectMethod === 'giroud' ? 'P' : 'W';

            rows.push(
                [axleLoadLabel, axleLoadSymbol, axleLoadValue, getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('tyre_inflation_pressure', 'Tyre Inflation Pressure'), 'p', unpavedData.load.tyreInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa)],
                [t('equivalent_standard_axle_load', 'ESAL'), 'Pa', unpavedData.load.esal?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf)],
                [t('number_of_passages', 'Number of Passages'), 'Np', unpavedData.load.numPassages?.toString() || '', ''],
                [t('allowable_rut_depth', 'Allowable Rut Depth'), 's', unpavedData.load.allowableRutDepth?.toString() || '', 'mm']
            );
        }

        // Subgrade inputs - only show the selected method
        if (unpavedData.subgrade) {
            const subgradeData = unpavedData.subgrade;
            const inputMethod = subgradeData.subgradeInputMethod;

            if (inputMethod === 'cbr') {
                rows.push(
                    [t('cbr_base_course', 'CBR Base Course'), 'CBRbc', subgradeData.cbrBaseCourse?.toString() || '', '%'],
                    [t('cbr_subgrade', 'CBR Subgrade'), 'CBRsg', subgradeData.cbrSubgrade?.toString() || '', '%']
                );
            } else if (inputMethod === 'modulus') {
                rows.push(
                    [t('ev2_base_course', 'Ev2 Base Course'), 'Ev2bc', subgradeData.ev2BaseCourse?.toString() || '', 'MPa'],
                    [t('ev2_subgrade', 'Ev2 Subgrade'), 'Ev2sg', subgradeData.ev2Subgrade?.toString() || '', 'MPa']
                );
            }
        }

        // Geogrid (always shown)
        if (unpavedData.geoGrid?.selectedGeogrid) {
            rows.push([t('selected_geogrid_product', 'Selected Geogrid Product'), '', unpavedData.geoGrid.selectedGeogrid, '']);
        }

        // Geotextile (only if selected)
        if (unpavedData.geoTextile?.selectedGeotextile && unpavedData.geoTextile.selectedGeotextile.trim() !== '') {
            rows.push([t('selected_geotextile_product', 'Selected Geotextile Product'), '', unpavedData.geoTextile.selectedGeotextile, '']);
        }

        return rows;
    }, [currentProjectType, currentProjectMethod, projectInputs, t, dynamicProject.data?.unit]);

    // --- Chart rendering logic ---
    const renderChartSection = () => {
        // For unpaved projects, check if chart data is available
        if (currentProjectType === EProjectType.unpaved) {
            const unpavedOutput = processedOutput as any;
            if (unpavedOutput?.chartData && Array.isArray(unpavedOutput.chartData) && unpavedOutput.chartData.length > 0) {
                const method = currentProjectMethod || 'giroud';
                logDebug('PavResults', `Rendering Unpaved (${method}) Chart with chartData`, undefined, LogTag.CHARTS);

                const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
                const hLabel = isImperial ? t('h_ft_axis_label', 'H (ft)') : t('h_m_axis_label', 'H (m)');
                const pLabel = isImperial ? t('p_psi_axis_label', 'P (psi)') : t('p_kpa_axis_label', 'P (kPa)');

                // Map chart data to the expected format
                const unreinforcedData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_UNR,
                }));

                const geotextileData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_GTX,
                }));

                const geogridData = unpavedOutput.chartData.map((item: any) => ({
                    x: item.h,
                    y: item.p_GG,
                }));

                return (
                    <PavementLineChartComponent
                        data={unpavedOutput.chartData} // Use the chartData directly from API
                        xAxisLabel={hLabel}
                        yAxisLabel={pLabel}
                        method={method === 'leng' ? 'leng' : 'giroud'} // Use the actual method (giroud or leng)
                    />
                );
            } else {
                logDebug('PavResults', 'Unpaved project - no chart data available', undefined, LogTag.CHARTS);
                return null;
            }
        }

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const hLabel = isImperial ? t('h_ft_axis_label', 'H (ft)') : t('h_m_axis_label', 'H (m)');
        const qLabel = isImperial ? t('q_psi_axis_label', 'q (psi)') : t('q_kpa_axis_label', 'q (kPa)');
        const cuLabel = isImperial ? t('cu_psi_axis_label', 'Cu (psi)') : t('cukpa_axis_label', 'Cu (kPa)');

        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && parkingDeckStaticChartData) {
            logDebug('CYResults', 'Rendering Parking Deck Static Chart', undefined, LogTag.CHARTS);
            return (
                <PavementLineChartComponent
                    data={parkingDeckStaticChartData.hStabilisedData} // Maps to legend dataKey 'reinforced'
                    reinforcedData={true}
                    xAxisLabel={cuLabel as string}
                    yAxisLabel={hLabel as string}
                    method={'paved'} // Standard paved method for regular legend labels
                />
            );
        } else if (currentProjectMethod === 'static' && staticChartData && staticChartData.vertPressure.length > 0) {
            logDebug('CYResults', 'Rendering Standard Static Chart for Container Yard', undefined, LogTag.CHARTS);
            return (
                <PavementLineChartComponent
                    data={staticChartData.stab} // stab data: { x: stabCapacity, y: height }
                    reinforcedData={true}
                    bcrData={true}
                    xAxisLabel={qLabel} // X-axis is now Pressure/Capacity
                    yAxisLabel={hLabel} // Y-axis is now Height
                    method={'paved'} // Standard paved method for regular legend labels
                />
            );
        }
        // Default: grafico classico
        return (
            <PavementLineChartComponent
                data={unreinforcedDeckTable}
                reinforcedData={true}
                bcrData={true}
                xAxisLabel={hLabel}
                yAxisLabel={t('fs_axis_label', 'FS') as string}
                method={'paved'} // Standard paved method for regular legend labels
            />
        );
    };

    const renderBcrInputTables = () => (
        <>
            <Grid
                container
                spacing={0}
                justifyContent="center"
                alignItems="center"
                className="
                    ntk-tabella
                ">
                <Grid item xs={8}>
                    <Typography
                        className="
                            table-title
                        ">
                        {t('load')}
                    </Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={loadResults} />
                </Grid>
            </Grid>
            <Grid
                container
                spacing={0}
                justifyContent="center"
                alignItems="center"
                className="
                    ntk-tabella
                ">
                <Grid item xs={8}>
                    <Typography
                        className="
                            table-title
                        ">
                        {t('subgrade')}
                    </Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={subgradeResults} />
                </Grid>
            </Grid>
            <Grid
                container
                spacing={0}
                justifyContent="center"
                alignItems="center"
                sx={{ borderBottom: '0 solid' }}
                className="
                    ntk-tabella
                ">
                <Grid item xs={8}>
                    <Typography
                        className="
                            table-title
                        ">
                        {t('embarkment_fill')}
                    </Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={embarkmentResults} />
                </Grid>
                <Grid item xs={5}></Grid>
            </Grid>
        </>
    );

    const renderStaticInputTables = () => (
        <Grid
            container
            spacing={0}
            justifyContent="center"
            alignItems="center"
            className="
                ntk-tabella
            ">
            <Grid item xs={8}>
                <Typography
                    className="
                        table-title
                    ">
                    {t('static_inputs_title', 'Input')}
                </Typography>
                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={staticInputResults} />
            </Grid>
        </Grid>
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Uniforma la larghezza del TireAndLayers del primo box (Unstabilized) in unpaved a quella di paved
    // rectWidth=360, forcedStageWidth=570 (360+210)
    const rectWidth = 360;
    const rectWidthGrid = hasGeotextileSelected ? 200 : 360;
    const rectWidthTextile = 200;
    const forcedStageWidth = 570;
    const forcedStageTextileWidth = rectWidthTextile + 120; // 320
    // TODO: devo passare anche la X
    const rectStartXOffsetTextile = -90;
    const [unstabilizedStageHeight, setUnstabilizedStageHeight] = useState<number | undefined>(undefined);

    return (
        <Grid container id="results-component-container" ref={containerRef}>
            {/* For unpaved projects (both giroud and leng): show 2 or 3 diagrams based on geotextile selection */}
            {currentProjectType === EProjectType.unpaved ? (
                <>
                    {/* TABELLA INPUTS - For Giroud and Leng methods */}
                    {/*
                    {(currentProjectMethod === 'giroud' || currentProjectMethod === 'leng') && unpavedInputsTable.length > 0 && (
                        <Grid
                            item
                            xs={12}
                            container
                            spacing={0}
                            justifyContent="center"
                            alignItems="center"
                            className="ntk-tabella"
                            sx={{ border: '0', borderBottom: '0' }}
                            style={{ marginBottom: '20px' }}>
                            <Grid item xs={8}>
                                <Typography className="table-title">{t('inputs_table', 'Inputs')}</Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={unpavedInputsTable} />
                            </Grid>
                        </Grid>
                    )}
                    */}
                    {/* Always show unstabilized diagram */}
                    <Grid item xs={12} md={hasGeotextileSelected ? 4 : 6} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div id="tire-layer-unstabilized" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px' }} className="cont-grafico">
                            {unpavedUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="unstabilized"
                                    baseCourse={unpavedUnstabilizedGraphicData.baseCourse}
                                    subgrade={unpavedUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={unpavedUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!unpavedUnstabilizedGraphicData.geogrid}
                                    geogrid={unpavedUnstabilizedGraphicData.geogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={unpavedUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    // forcedStageWidth={forcedStageWidth}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={false}
                                    onStageHeightCalculated={h => setUnstabilizedStageHeight(h)}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>
                    </Grid>

                    {/* Always show geogrid diagram */}
                    <Grid item xs={12} md={hasGeotextileSelected ? 4 : 6} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div
                            id="tire-layer-geogrid"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', minHeight: '300px' }}
                            className="
                                cont-grafico
                            ">
                            {unpavedGeogridGraphicData ? (
                                <TireAndLayers
                                    stageType="geogrid"
                                    unstabilizedStageHeight={unstabilizedStageHeight}
                                    baseCourse={unpavedGeogridGraphicData.baseCourse}
                                    subgrade={unpavedGeogridGraphicData.subgrade}
                                    rightDimensionValue={unpavedGeogridGraphicData.rightDimensionValue}
                                    savingPercentage={unpavedGeogridGraphicData.savingPercentage}
                                    geogrid={unpavedGeogridGraphicData.geogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!unpavedGeogridGraphicData.geogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={unpavedGeogridGraphicData.unitLabel}
                                    customStageHeight={unreinforcedStageHeight}
                                    alignFromBottom={true}
                                    targetSubgradeY={unreinforcedSubgradeY}
                                    hideLayerTitles={hasGeotextileSelected ? true : false}
                                    projectType={currentProjectType}
                                    // forcedStageWidth={hasGeotextileSelected ? forcedStageTextileWidth : forcedStageWidth}
                                    rectWidth={rectWidthGrid}
                                    rectStartXOffset={hasGeotextileSelected ? -60 : 0}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>
                    </Grid>

                    {/* Conditionally show geotextile diagram only if GEOTEXTILE was selected */}
                    {hasGeotextileSelected && (
                        <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography align="center" variant="subtitle1" gutterBottom>
                                {t('geotextile_title', 'GEOTEXTILE')}
                            </Typography>
                            <div
                                id="tire-layer-geotextile"
                                style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', minHeight: '300px' }}
                                className="
                                    cont-grafico
                                ">
                                {unpavedGeotextileGraphicData ? (
                                    <TireAndLayers
                                        stageType="geotextile"
                                        unstabilizedStageHeight={unstabilizedStageHeight}
                                        baseCourse={unpavedGeotextileGraphicData.baseCourse}
                                        subgrade={unpavedGeotextileGraphicData.subgrade}
                                        rightDimensionValue={unpavedGeotextileGraphicData.rightDimensionValue}
                                        savingPercentage={unpavedGeotextileGraphicData.savingPercentage}
                                        geogrid={unpavedGeotextileGraphicData.geogrid}
                                        geotextileLabel={selectedGeotextileProduct || 'Geotextile'}
                                        showLeftDimension={false}
                                        showRightDimension={true}
                                        showSavingBox={true}
                                        showGeogrid={false}
                                        showGeotextile={true}
                                        showTire={true}
                                        showLayers={true}
                                        unitLabel={unpavedGeotextileGraphicData.unitLabel}
                                        customStageHeight={unreinforcedStageHeight}
                                        alignFromBottom={true}
                                        targetSubgradeY={unreinforcedSubgradeY}
                                        hideLayerTitles={true}
                                        projectType={currentProjectType}
                                        // forcedStageWidth={forcedStageTextileWidth}
                                        rectWidth={rectWidthTextile}
                                        rectStartXOffset={-60} // approssimativamente è quanto occupano i titoli di sinistra
                                    />
                                ) : (
                                    <Typography>Loading diagram...</Typography>
                                )}
                            </div>
                        </Grid>
                    )}
                </>
            ) : (
                // TODO: ma servono questi due??
                // For other project types: show 2 diagrams (unreinforced and reinforced)
                <>
                    <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('unreinforced_diagram_title', 'Unreinforced')}
                        </Typography>
                        <div
                            id="tire-layer-unreinforced"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}
                            className="
                                cont-grafico
                            ">
                            {unreinforcedGraphicData ? (
                                <TireAndLayers
                                    baseCourse={unreinforcedGraphicData.baseCourse}
                                    subgrade={unreinforcedGraphicData.subgrade}
                                    rightDimensionValue={unreinforcedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!unreinforcedGraphicData.geogrid}
                                    geogrid={unreinforcedGraphicData.geogrid}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={unreinforcedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    // forcedStageWidth={forcedStageWidth}
                                    onStageHeightCalculated={h => {
                                        setUnreinforcedStageHeight(h);
                                    }}
                                    onSubgradeYCalculated={y => {
                                        logDebug('PavResults', 'SubgradeY sinistro:', y, LogTag.CHARTS);
                                        setUnreinforcedSubgradeY(y);
                                    }}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('reinforced_diagram_title', 'Reinforced')}
                        </Typography>
                        <div
                            id="tire-layer-reinforced"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}
                            className="
                                cont-grafico
                            ">
                            {reinforcedGraphicData ? (
                                <TireAndLayers
                                    baseCourse={reinforcedGraphicData.baseCourse}
                                    subgrade={reinforcedGraphicData.subgrade}
                                    rightDimensionValue={reinforcedGraphicData.rightDimensionValue}
                                    savingPercentage={reinforcedGraphicData.savingPercentage}
                                    geogrid={reinforcedGraphicData.geogrid}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!reinforcedGraphicData.geogrid}
                                    showTire={true}
                                    showLeftDimension={false}
                                    unitLabel={reinforcedGraphicData.unitLabel}
                                    customStageHeight={unreinforcedStageHeight}
                                    alignFromBottom={true}
                                    targetSubgradeY={unreinforcedSubgradeY}
                                    hideLayerTitles={false}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidth}
                                    onStageHeightCalculated={h => {
                                        // logDebug('PavResults', 'Altezza destro:', h, LogTag.CHARTS);
                                        // console.log('[PavResults/Reinforced] stageHeight:', h);
                                    }}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>
                    </Grid>
                </>
            )}
            <Grid mt={3} container spacing={0} justifyContent="center" alignItems="center" sx={{ border: '0 solid' }} className="ntk-tabella">
                <Grid item xs={8}>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={deckResults} />
                </Grid>
            </Grid>
            <Grid
                container
                spacing={0}
                style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                className="
                    ntk-grafico
                ">
                <Grid item xs={11} style={{ marginBottom: 30 }} id="line-chart-results">
                    {renderChartSection()}
                </Grid>
            </Grid>
            {/* Check if projectInputs are available to decide rendering these tables */}
            {projectInputs && currentProjectMethod === 'bcr' && renderBcrInputTables()}
            {projectInputs && currentProjectMethod === 'static' && renderStaticInputTables()}
        </Grid>
    );
};

export default Results;
